/**
 * @fileOverview Client script that handles the subscription modals.
 * @see ./README.md
 */

import { actionNames } from 'shared/store/ducks/userSession'
import { HIDE_MODAL } from 'shared/store/ducks/events'
import { LOGIN_FORM_SUBMITTED } from 'shared/consts'
import { SHOW_ABO_MODAL_ID } from 'views/consts'
import { OFFER_TYPE_SAE } from 'shared/consts/marketplace'
import { PAGE_TYPE_CONFIRMATION, PAGE_TYPE_PRODUCT } from 'shared/consts/page'

import logger from 'shared/services/logger'
import store, { subscribe } from 'shared/store'
import Storage from 'views/assets/scripts/services/storage/storage'
import MountPointsClass from 'shared/utils/mountPoints'
import blockUI from 'views/assets/scripts/blockUI'
import runClientSideScriptForSubscription, {
  getSubscriptionElements,
  showLoginFormWithErrorMessage,
  showLoginForm,
  getProductIDForProductListing
} from 'views/assets/scripts/subscription'

const storage = new Storage('sessionStorage')
const isStorageAvailable = storage.isAvailable()
const { rootElement, createSubscriptionLink, createSubscriptionLinkModal } =
  getSubscriptionElements()

const containsHash = urlHash => window.location.hash.endsWith(urlHash)

const handleSubscriptionLinkClick = async (
  { productId, pageType, mountPointsInstance },
  link
) => {
  const unblockUI = blockUI()

  try {
    // load once the subscription client script
    const subscriptionClientContainer = await import(
      /* webpackChunkName: "SubscriptionClientContainerWithRender" */
      './index'
    )

    if (isStorageAvailable) {
      storage.set('showSubscriptionModalOnPageLoad', false)
    }

    const finalProductId =
      pageType === PAGE_TYPE_CONFIRMATION
        ? getProductIDForProductListing(link)
        : productId

    subscriptionClientContainer.default({
      container: rootElement,
      productId: finalProductId,
      mode: 'CREATE_OR_EDIT_SUBSCRIPTION',
      onSuccess: response => {
        const { shippingAddressId = null } = response
        window.location.href = mountPointsInstance.getAccountSubscriptions({
          shippingId: shippingAddressId,
          subscriptionAction: 'subscriptionCreated'
        })
      }
    })
  } catch (error) {
    logger.error('Error on subscription link click ', {
      error
    })
  }

  unblockUI()
}

const mountSubscriptionToDom = async (userSession, mountPointsInstance) => {
  const { isLoggedIn, user: { customerNumber: id } = {} } = userSession
  let {
    pageProperties: { productId, pageType }
  } = store.getPublicRuntimeConfig()

  window.shopCustomerId = id

  const isEligibleProductForSubscription = Boolean(
    isLoggedIn && createSubscriptionLink
  )
  if (isEligibleProductForSubscription) {
    const createSubscriptionLinks = createSubscriptionLink.concat(
      createSubscriptionLinkModal
    )

    const handler = handleSubscriptionLinkClick.bind(null, {
      productId,
      pageType,
      mountPointsInstance
    })
    createSubscriptionLinks.forEach(link => {
      link.addEventListener('click', () => handler(link))
    })
  }

  const isLoginFormSubmitted = containsHash(LOGIN_FORM_SUBMITTED)
  const isRedirectWithAboModal = containsHash(SHOW_ABO_MODAL_ID)
  const showSubscriptionModalOnPageLoad = storage.get(
    'showSubscriptionModalOnPageLoad'
  )
  const shouldShowSubscriptionModalOnPageLoad =
    isEligibleProductForSubscription &&
    [
      isRedirectWithAboModal,
      isLoginFormSubmitted,
      showSubscriptionModalOnPageLoad && isLoginFormSubmitted
    ].some(Boolean)
  if (shouldShowSubscriptionModalOnPageLoad) {
    createSubscriptionLinkModal.click()
  }

  const shouldShowLoggedInFormOnPageLoad = !isLoggedIn && isRedirectWithAboModal
  if (shouldShowLoggedInFormOnPageLoad) {
    showLoginForm()
  }

  const shouldSFormErrorsOnPageLoad = !isLoggedIn && isLoginFormSubmitted
  if (shouldSFormErrorsOnPageLoad) {
    showLoginFormWithErrorMessage()
  }
}

export default () => {
  if (!createSubscriptionLinkModal) {
    return
  }

  subscribe.after.once(actionNames.setUserSession, ({ userSession }) => {
    const {
      publicConfig: {
        subscriptionServiceAPIURL: subscriptionApiUrl,
        mountPoints,
        tenant,
        language
      },
      pageProperties: { pageType, coreShopEnvironment }
    } = store.getPublicRuntimeConfig()

    const { isLoggedIn } = userSession
    const { buyboxSubscriptionLink } = getSubscriptionElements()
    if (subscriptionApiUrl) {
      window.Red = {
        SETTINGS: {
          subscriptionApiUrl
        }
      }
    }

    const mountPointsInstance = new MountPointsClass({
      mountPoints,
      tenant,
      language,
      coreShopEnvironment
    })

    if (!isLoggedIn && buyboxSubscriptionLink) {
      buyboxSubscriptionLink.addEventListener('click', () => {
        if (isStorageAvailable) {
          storage.set('showSubscriptionModalOnPageLoad', true)
        } else {
          // fallback for browsers which don't support session storage
          window.location.href = mountPointsInstance.getLogin()
        }
      })

      subscribe.after(HIDE_MODAL, () => {
        if (isStorageAvailable) {
          storage.set('showSubscriptionModalOnPageLoad', false)
        }
      })
    }

    if (pageType === PAGE_TYPE_PRODUCT || pageType === PAGE_TYPE_CONFIRMATION) {
      mountSubscriptionToDom(userSession, mountPointsInstance)
      runClientSideScriptForSubscription(userSession)
    } else {
      createSubscriptionLinkModal.addEventListener('click', () => {
        const state = store.getState()
        const {
          events: { productUrl, offerType }
        } = state

        const shouldRedirectToPDP = offerType === OFFER_TYPE_SAE

        if (shouldRedirectToPDP) {
          window.location.href = `${productUrl}#${SHOW_ABO_MODAL_ID}`
        }
      })
    }
  })
}
